import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { SignUpHeader } from '../../components/SignUpHeader';
import { ProSignUpSuccess } from '../../components/ProSignUpSuccess';
import { useAuth } from '../../../lib/use-auth';
import { getToken, patchUser } from '../../../lib/auth';
import { getMe } from '../../../lib/professional-api';

/**
 * Post-signup "Thank you, Pro!" waiting screen. The pro normally lands
 * here once after submitting the signup form, sees the "check your
 * inbox" copy + Resend button, clicks the verification link in their
 * email, and never comes back to this page.
 *
 * Three real-world states this page must handle correctly:
 *
 *   1. Anonymous mid-signup (no token) — render the waiting screen.
 *      `location.state.email` populates the address line.
 *   2. Logged-in pro whose email is NOT yet verified — render the
 *      waiting screen. This is the canonical happy path; we MUST NOT
 *      flash a loading spinner in front of it because that would
 *      confuse a user who literally just submitted the signup form.
 *   3. Logged-in pro whose email IS already verified (came back via
 *      a bookmark / stale tab / clicked the link in another tab) —
 *      redirect to the same destination RequireOnboardedPro would
 *      pick: /professional/service-offer when onboarding_completed_at
 *      is null, /professional/dashboard otherwise. This is the bug
 *      surfaced 2026-06-04.
 *
 * Implementation: when a token is present, we kick off a single
 * getMe() probe on mount. We keep rendering the waiting screen (the
 * common case) until the probe returns; if it confirms verified, we
 * patchUser() the fresh fields and <Navigate> to the canonical target.
 * The probe is cancelled on unmount or mid-flight logout to avoid a
 * stale Navigate after the user has clicked Logout.
 */
export default function ProSignUpSuccessPage() {
  const { isAuthenticated, user } = useAuth();
  // null = stay on this page (waiting UI renders immediately, no
  // spinner flash on the happy path); a string = navigate there.
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Auth flipped to false (e.g. Logout clicked while target was
      // set) — clear any stale target so we don't fire a Navigate to
      // a protected route on a token-less session.
      setTarget(null);
      return;
    }
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        // Mid-flight logout: a Logout click between the request being
        // sent and resolving would clear the token. Don't write back
        // to a cleared session and don't fire a Navigate that would
        // only get bounced by the next route guard anyway.
        if (!getToken()) return;
        const info: any = res?.info ?? {};
        const u = info.user ?? info;
        // Mirror RequireOnboardedPro's defensive fallback: trust the
        // server's value, fall through to the locally-cached value
        // only if the response omits the field.
        const verifiedAt: string | null =
          u?.email_verified_at ?? user?.email_verified_at ?? null;
        // Hydrate the local cache from the server in BOTH directions
        // (verified or not) so downstream guards never act on a stale
        // localStorage copy. Same pattern as RequireOnboardedPro.
        patchUser({
          status: u?.status,
          email_verified_at: verifiedAt,
        });
        if (!verifiedAt) {
          // Server confirms unverified. Stay on this page.
          return;
        }
        // Pick the same destination RequireOnboardedPro would pick for
        // this state. onboarding_completed_at is the canonical "post-
        // signup walk done" flag — its absence means the pro still
        // owes us the services/location/certifications/insurance
        // wizard, which lives at /professional/service-offer. Setup-
        // payouts is the wizard's step-10 hand-off, NOT the re-entry
        // point.
        setTarget(
          info.onboarding_completed_at
            ? '/professional/dashboard'
            : '/professional/service-offer',
        );
      })
      .catch(() => {
        // Network / 401 / anything else — stay on the waiting screen
        // so the pro can still hit Resend manually. The Resend handler
        // has its own already-verified branch as a second line of
        // defence (see ProSignUpSuccess.tsx).
      });
    return () => {
      cancelled = true;
    };
    // user is intentionally omitted from deps — we only need its
    // value as a fallback at the moment getMe resolves, not as a
    // re-run trigger (patchUser will itself bump the user store).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Render-time logout guard: between setTarget firing and the next
  // effect running, the user might log out — DON'T emit a <Navigate>
  // to a protected route in that window. `isAuthenticated` flips in
  // the same render so this check is reliable.
  if (isAuthenticated && typeof target === 'string') {
    return <Navigate to={target} replace />;
  }

  return (
    <div className="min-h-screen bg-[#fcfaf7] flex flex-col items-center">
      {/* SignUpHeader renders the pro Logout chip when a token exists.
          On the anonymous mid-signup path (state #1) the right-hand
          side is empty by design. */}
      <SignUpHeader isProfessional={true} mode="signin" />

      <main className="flex-1 flex items-center justify-center w-full px-4 py-20">
        <ProSignUpSuccess />
      </main>
    </div>
  );
}
